import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';

import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { ErrorBoxComponent } from 'components/Error/ErrorBoxComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { PATH_CONSTANT } from 'constant/PathConstant';
import { useGetAllKTreeDefinitionList } from 'services/v1/SystemTenant/AROKMS/KTreeBuilderService';
import { useTableDefinitionDropdownBySubjectId } from 'services/v1/SystemTenant/AROKMS/TableDefinitionService';
import { useGetFunctionDefinitionSubjectDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import {
  useCreateAccountabilityDefinition,
  useGetAccountabilityDefinition,
  useUpdateAccountabilityDefinition,
} from 'services/v1/SystemTenant/AROChart/AccountabilityDefinitionService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { AccountabilityDefinitionRequest } from 'types/api/SystemTenant/AROChart/AccountabilityDefinitionTypes';

const sectionCardSx = {
  bgcolor: '#fff',
  border: '1px solid #E3EBF6',
  borderRadius: 1.5,
  p: 2.5,
};

const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    py: 0,
  },
};

let localLevelKey = 0;

function createLevelKey() {
  localLevelKey += 1;
  return `accountability-level-${localLevelKey}`;
}

interface AccountabilityLevelFormValue {
  key: string;
  level: number | string;
  subject: AutoCompleteItem | null;
  nodeTableDefinition: AutoCompleteItem | null;
  accountableTableDefinition: AutoCompleteItem | null;
}

interface AccountabilityFormValue {
  name: string;
  kTreeDefinition: AutoCompleteItem | null;
  accountableSubject: AutoCompleteItem | null;
  levels: AccountabilityLevelFormValue[];
}

const createEmptyLevel = (level: number): AccountabilityLevelFormValue => ({
  key: createLevelKey(),
  level,
  subject: null,
  nodeTableDefinition: null,
  accountableTableDefinition: null,
});

const initialValues: AccountabilityFormValue = {
  name: '',
  kTreeDefinition: null,
  accountableSubject: null,
  levels: [createEmptyLevel(0)],
};

function isSameOption(option: AutoCompleteItem, value: AutoCompleteItem) {
  return option.value.toString() === value.value.toString();
}

interface LevelEditorProps {
  value: AccountabilityLevelFormValue;
  subjectOptions: AutoCompleteItem[];
  submitted: boolean;
  duplicateLevel: boolean;
  canRemove: boolean;
  onChange: (value: AccountabilityLevelFormValue) => void;
  onRemove: () => void;
}

function LevelEditor({
  value,
  subjectOptions,
  submitted,
  duplicateLevel,
  canRemove,
  onChange,
  onRemove,
}: LevelEditorProps) {
  const subjectId = value.subject?.value?.toString();
  const { data: tableDefinitions, isLoading: isLoadingTableDefinitions } =
    useTableDefinitionDropdownBySubjectId(subjectId);
  const tableDefinitionOptions = tableDefinitions?.data ?? [];
  const parsedLevel = Number(value.level);
  const invalidLevel = value.level === '' || !Number.isInteger(parsedLevel) || parsedLevel < 0;

  const updateValue = (patch: Partial<AccountabilityLevelFormValue>) => {
    onChange({ ...value, ...patch });
  };

  return (
    <Box
      sx={{
        border: '1px solid #DCE6F3',
        borderRadius: 1.5,
        p: 2,
        bgcolor: '#FBFCFF',
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={1.5}>
        <Typography variant='subtitle2' fontWeight={800} color='#3B4797'>
          Level configuration
        </Typography>
        <Tooltip title={canRemove ? 'Remove level' : 'At least one level is required'}>
          <span>
            <IconButton size='small' color='error' disabled={!canRemove} onClick={onRemove}>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '140px minmax(180px, 1fr) minmax(220px, 1fr) minmax(220px, 1fr)',
          },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <TextField
          label='Level Number'
          type='number'
          size='small'
          value={value.level}
          inputProps={{ min: 0, step: 1 }}
          onChange={(event) => updateValue({ level: event.target.value })}
          error={submitted && (invalidLevel || duplicateLevel)}
          helperText={
            submitted &&
            (duplicateLevel ? 'Level numbers must be unique' : invalidLevel ? 'Enter 0 or greater' : undefined)
          }
        />

        <Autocomplete
          options={subjectOptions}
          value={value.subject}
          isOptionEqualToValue={isSameOption}
          getOptionLabel={(option) => option.label}
          onChange={(_, subject) =>
            updateValue({
              subject,
              nodeTableDefinition: null,
              accountableTableDefinition: null,
            })
          }
          sx={autocompleteSx}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Level Subject'
              size='small'
              error={submitted && !value.subject}
              helperText={submitted && !value.subject ? 'Select a subject' : undefined}
            />
          )}
        />

        <Autocomplete
          options={tableDefinitionOptions}
          value={value.nodeTableDefinition}
          isOptionEqualToValue={isSameOption}
          getOptionLabel={(option) => option.label}
          disabled={!subjectId || isLoadingTableDefinitions}
          loading={isLoadingTableDefinitions}
          onChange={(_, nodeTableDefinition) => updateValue({ nodeTableDefinition })}
          sx={autocompleteSx}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Node Table Definition'
              size='small'
              placeholder={!subjectId ? 'Select a subject first' : undefined}
              error={submitted && !value.nodeTableDefinition}
              helperText={submitted && !value.nodeTableDefinition ? 'Select the node field' : undefined}
            />
          )}
        />

        <Autocomplete
          options={tableDefinitionOptions}
          value={value.accountableTableDefinition}
          isOptionEqualToValue={isSameOption}
          getOptionLabel={(option) => option.label}
          disabled={!subjectId || isLoadingTableDefinitions}
          loading={isLoadingTableDefinitions}
          onChange={(_, accountableTableDefinition) => updateValue({ accountableTableDefinition })}
          sx={autocompleteSx}
          renderInput={(params) => (
            <TextField
              {...params}
              label='Accountable Table Definition'
              size='small'
              placeholder={!subjectId ? 'Select a subject first' : undefined}
              error={submitted && !value.accountableTableDefinition}
              helperText={submitted && !value.accountableTableDefinition ? 'Select the accountable field' : undefined}
            />
          )}
        />
      </Box>
    </Box>
  );
}

interface AccountabilityDefinitionFormProps {
  definitionId?: string;
}

export default function AccountabilityDefinitionForm({ definitionId }: AccountabilityDefinitionFormProps) {
  const navigate = useNavigate();
  const isEditing = Boolean(definitionId);
  const [values, setValues] = useState<AccountabilityFormValue>(initialValues);
  const [submitted, setSubmitted] = useState(false);

  const {
    data: definitionDetails,
    isLoading: isLoadingDefinition,
    isError: isDefinitionError,
    error: definitionError,
  } = useGetAccountabilityDefinition(definitionId);
  const {
    data: kTreeDefinitions,
    isLoading: isLoadingKTrees,
    isError: isKTreeError,
    error: kTreeError,
  } = useGetAllKTreeDefinitionList();
  const {
    data: subjects,
    isLoading: isLoadingSubjects,
    isError: isSubjectError,
    error: subjectError,
  } = useGetFunctionDefinitionSubjectDropdown();
  const { mutate: createDefinition, isLoading: isCreating } = useCreateAccountabilityDefinition();
  const { mutate: updateDefinition, isLoading: isUpdating } = useUpdateAccountabilityDefinition();

  useEffect(() => {
    if (!definitionDetails?.data) return;

    const definition = definitionDetails.data;
    setValues({
      name: definition.name,
      kTreeDefinition: {
        value: definition.kTreeDefinition.id,
        label: definition.kTreeDefinition.label,
      },
      accountableSubject: {
        value: definition.accountableSubject.id,
        label: definition.accountableSubject.label,
      },
      levels: definition.levels.map((level) => ({
        key: level.id || createLevelKey(),
        level: level.level,
        subject: { value: level.subject.id, label: level.subject.label },
        nodeTableDefinition: {
          value: level.nodeTableDefinition.id,
          label: level.nodeTableDefinition.label,
        },
        accountableTableDefinition: {
          value: level.accountableTableDefinition.id,
          label: level.accountableTableDefinition.label,
        },
      })),
    });
  }, [definitionDetails]);

  const levelNumberCounts = useMemo(
    () =>
      values.levels.reduce<Record<string, number>>((counts, level) => {
        const key = level.level.toString();
        counts[key] = (counts[key] ?? 0) + 1;
        return counts;
      }, {}),
    [values.levels]
  );

  const isFormValid = useMemo(() => {
    if (!values.name.trim() || !values.kTreeDefinition || !values.accountableSubject || values.levels.length === 0) {
      return false;
    }

    return values.levels.every((level) => {
      const parsedLevel = Number(level.level);
      return (
        level.level !== '' &&
        Number.isInteger(parsedLevel) &&
        parsedLevel >= 0 &&
        levelNumberCounts[level.level.toString()] === 1 &&
        Boolean(level.subject && level.nodeTableDefinition && level.accountableTableDefinition)
      );
    });
  }, [levelNumberCounts, values]);

  const updateLevel = (key: string, value: AccountabilityLevelFormValue) => {
    setValues((current) => ({
      ...current,
      levels: current.levels.map((level) => (level.key === key ? value : level)),
    }));
  };

  const addLevel = () => {
    const currentLevelNumbers = values.levels
      .map((level) => Number(level.level))
      .filter((level) => Number.isInteger(level) && level >= 0);
    const nextLevel = currentLevelNumbers.length > 0 ? Math.max(...currentLevelNumbers) + 1 : 0;

    setValues((current) => ({
      ...current,
      levels: [...current.levels, createEmptyLevel(nextLevel)],
    }));
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!isFormValid || !values.kTreeDefinition || !values.accountableSubject) return;

    const payload: AccountabilityDefinitionRequest = {
      name: values.name.trim(),
      kTreeDefinitionId: values.kTreeDefinition.value.toString(),
      accountableSubjectId: values.accountableSubject.value.toString(),
      levels: values.levels
        .map((level) => ({
          level: Number(level.level),
          subjectId: level.subject!.value.toString(),
          nodeTableDefinitionId: level.nodeTableDefinition!.value.toString(),
          accountableTableDefinitionId: level.accountableTableDefinition!.value.toString(),
        }))
        .sort((left, right) => left.level - right.level),
    };

    const onSuccess = () => {
      toast.success(`Accountability definition ${isEditing ? 'updated' : 'created'} successfully`);
      navigate(PATH_CONSTANT.SYSTEM.CHART.ACCOUNTABILITY);
    };

    if (definitionId) {
      updateDefinition({ id: definitionId, data: payload }, { onSuccess });
    } else {
      createDefinition(payload, { onSuccess });
    }
  };

  const isLoading = isLoadingDefinition || isLoadingKTrees || isLoadingSubjects;
  const isSaving = isCreating || isUpdating;
  const loadError = definitionError ?? kTreeError ?? subjectError;
  const hasLoadError = isDefinitionError || isKTreeError || isSubjectError;
  const title = isEditing ? 'Edit Accountability Definition' : 'Add Accountability Definition';

  return (
    <MainLayoutComponent pageTitle={title} breadcrumbs={['Dashboard', 'ARO Chart', 'Accountability']}>
      {hasLoadError ? (
        <ErrorBoxComponent error={loadError} />
      ) : isLoading ? (
        <Stack height={450} alignItems='center' justifyContent='center' bgcolor='#fff'>
          <ActivityIndicator />
        </Stack>
      ) : (
        <Stack gap={2} p={2} bgcolor='#F8FAFF'>
          {submitted && !isFormValid && (
            <Alert severity='error'>
              Complete every required field and use a unique, non-negative number for each level.
            </Alert>
          )}

          <Box sx={sectionCardSx}>
            <Typography variant='body1' component='h2' fontWeight='bold' color='#3B4797'>
              Definition
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.25}>
              Select the hierarchy and the subject whose members can be accountable.
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(3, minmax(220px, 1fr))',
                },
                gap: 2,
              }}
            >
              <TextField
                label='Name'
                size='small'
                value={values.name}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                error={submitted && !values.name.trim()}
                helperText={submitted && !values.name.trim() ? 'Enter a definition name' : undefined}
                placeholder='e.g. Organisation Accountability'
              />

              <Autocomplete
                options={kTreeDefinitions?.data ?? []}
                value={values.kTreeDefinition}
                isOptionEqualToValue={isSameOption}
                getOptionLabel={(option) => option.label}
                onChange={(_, kTreeDefinition) => setValues((current) => ({ ...current, kTreeDefinition }))}
                sx={autocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='K-Tree Definition'
                    size='small'
                    error={submitted && !values.kTreeDefinition}
                    helperText={submitted && !values.kTreeDefinition ? 'Select a K-Tree' : undefined}
                  />
                )}
              />

              <Autocomplete
                options={subjects?.data ?? []}
                value={values.accountableSubject}
                isOptionEqualToValue={isSameOption}
                getOptionLabel={(option) => option.label}
                onChange={(_, accountableSubject) => setValues((current) => ({ ...current, accountableSubject }))}
                sx={autocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label='Accountable Subject'
                    size='small'
                    error={submitted && !values.accountableSubject}
                    helperText={submitted && !values.accountableSubject ? 'Select the accountable subject' : undefined}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={sectionCardSx}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={1.5} mb={2}>
              <Box>
                <Typography variant='body1' component='h2' fontWeight='bold' color='#3B4797'>
                  Accountability Levels
                </Typography>
                <Typography variant='body2' color='text.secondary' mt={0.25}>
                  Map each K-Tree level to its subject, display field, and accountable-person field.
                </Typography>
              </Box>
              <Button variant='main-table-panel-border' startIcon={<AddCircleOutlineIcon />} onClick={addLevel}>
                Add Level
              </Button>
            </Stack>

            <Stack gap={1.5}>
              {values.levels.map((level) => (
                <LevelEditor
                  key={level.key}
                  value={level}
                  subjectOptions={subjects?.data ?? []}
                  submitted={submitted}
                  duplicateLevel={levelNumberCounts[level.level.toString()] > 1}
                  canRemove={values.levels.length > 1}
                  onChange={(updatedLevel) => updateLevel(level.key, updatedLevel)}
                  onRemove={() =>
                    setValues((current) => ({
                      ...current,
                      levels: current.levels.filter((currentLevel) => currentLevel.key !== level.key),
                    }))
                  }
                />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              ...sectionCardSx,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Stack direction='row' gap={1.5}>
              <Button
                variant='main-table-panel-border'
                disabled={isSaving}
                onClick={() => navigate(PATH_CONSTANT.SYSTEM.CHART.ACCOUNTABILITY)}
              >
                Cancel
              </Button>
              <LoadingButton variant='main-table-panel' loading={isSaving} onClick={handleSave}>
                Save Definition
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      )}
    </MainLayoutComponent>
  );
}
